/**
 * Finding where a chapter's exercises are printed, and where its facit is.
 *
 * This used to be the front half of a transcription pipeline: parse the chapter, show the owner
 * every row it produced, let him correct each one, and store the result as the book. That whole
 * idea is gone — nobody reads a typed version of a page they own — and what is left is the only
 * part of it the app still needs.
 *
 * **What the app keeps of an exercise is a LINE RANGE, not its prose.** `pdf-layout` reports a
 * box per line of extracted text, and saying which lines became exercise 1042 is what turns the
 * rendered page into something clickable. The prompt itself is not stored anywhere: the owner
 * reads it off the page, and clicking it clips the page.
 *
 * The facit is the exception, and it is the only one. An answer is a line of text worth keeping
 * because the point of it is to be compared against what you wrote — so the answers ARE paired
 * onto the exercises here, exactly as they always were, and the reveal is unchanged.
 *
 * It is deliberately separate from PDF extraction. Extraction is a solved problem with a library
 * behind it; SEGMENTATION is where the judgement lives, and keeping it pure means it can be
 * tested against text whose correct answer is already known.
 */

export interface ParsedExercise {
  n: number;
  text: string;
  parts: string[];
  /** Nivå 1–4 where the book bands them, null where no band was seen. */
  level: 1 | 2 | 3 | 4 | null;
  /** The book stars exercises with a worked solution. */
  starred: boolean;
  /** The section heading last printed above it, where the book prints one. */
  section: ParsedSection | null;
  /** What the facit prints for it. Null for a "visa att" with nothing to check against. */
  answer: string | string[] | null;
  /**
   * The half-open range of LINES of the parsed text this block was read from.
   *
   * This is the whole bridge between the segmenter and the rendered page. Extraction hands back
   * a box per line of text; the segmenter knows which lines became which exercise; together they
   * say where on the page exercise 1042 is printed, which is what makes it clickable. Nothing
   * else in the app can recover it — the text has already been joined by the time anyone else
   * sees it.
   */
  lines: { from: number; to: number };
}

export interface ParsedChapter {
  exercises: ParsedExercise[];
  /** Where the answer section started, so a caller can say the facit was found. */
  facitAt: number | null;
}

/**
 * A line that starts an exercise.
 *
 * The book writes `1001.` at the start of a line. The trailing dot matters: without it every
 * year, page number and stray figure label in the text becomes an exercise. Three to five digits
 * because that is what the numbering schemes in use produce.
 *
 * The star may sit on EITHER side of the number, and it is not always an asterisk. Books that
 * mark a worked solution print it before the number as often as after it, and a real extraction
 * hands over whichever glyph the book set — so `*1006.` was read as no exercise at all and its
 * whole question was appended to 1005, answers and all. That is the worst failure this file can
 * produce, because nothing downstream can tell that it happened.
 */
const EXERCISE_START = /^\s*([*\u2605\u2606\u2731]?)\s*(\d{3,5})\s*\.\s*([*\u2605\u2606\u2731]?)\s*/;

/** `a)` `b)` — the book's sub-part marker, at the start of a line or after whitespace. */
const PART_START = /(?:^|\s)([a-h])\s*\)\s*/g;

/**
 * The heading that separates the exercises from their answers.
 *
 * Deliberately loose, because a real one says "Facit kapitel 1" rather than "Facit" — and when
 * that line is not recognised the whole answer section is parsed as exercises, so the chapter
 * comes out with every number twice and not one answer attached. Being loose costs nothing here
 * because `splitAtFacit` proves the candidate before it uses it.
 */
const FACIT_HEADING = /^\s*(?:\d+(?:\.\d+)*\s+)?(?:facit|svar|answers)\b[^.!?]{0,28}$/i;

/** Every exercise number a region of text starts a line with. */
const numbersIn = (text: string): number[] =>
  text.split('\n').map(line => EXERCISE_START.exec(line)).filter(Boolean)
    .map(match => Number(match![2]));

/** `Nivå 1` and friends. */
const LEVEL_HEADING = /^\s*Niv[åa]\s*([1-4])\s*$/i;

/**
 * A section heading: `1.1.2 Enhetscirkeln och trigonometriska ekvationer`.
 *
 * It ends the exercise above it, exactly as a level heading does. Without this the heading was
 * read as the continuation of the last question on the page — 1008's prompt ended
 * "…den trigonometriska ettan. 1.1.2 Enhetscirkeln och trigonometriska ekvationer" — which is
 * both wrong and the kind of wrong nobody notices, because the exercise still reads plausibly.
 *
 * Deliberately narrow. It must be a dotted number, then words, on a line of its own, with no
 * sentence-ending punctuation: `1.1.2 Radianer` is a heading and `0.5 kg mjöl behövs.` is not.
 */
const SECTION_HEADING = /^\s*(\d{1,2}(?:\.\d{1,2}){1,3})\s+(\p{Lu}[^.!?]{2,64})\s*$/u;

export interface ParsedSection {
  number: string;
  title: string;
}

const clean = (text: string): string => text.replace(/\s+/g, ' ').trim();

/**
 * Split a block into its lettered parts.
 *
 * Returns the lead-in text and the parts separately, because the lead-in is the instruction that
 * applies to all of them — "Lös ekvationerna nedan fullständigt" — and repeating it on each part
 * would be wrong.
 */
export function splitParts(block: string): { text: string; parts: string[] } {
  const marks: { letter: string; at: number; end: number }[] = [];
  PART_START.lastIndex = 0;
  let match = PART_START.exec(block);
  while (match) {
    marks.push({ letter: match[1]!, at: match.index, end: match.index + match[0].length });
    match = PART_START.exec(block);
  }

  // Parts must run a, b, c… in order. A stray "a)" inside prose does not, and a single "b)" with
  // no "a)" before it is punctuation rather than structure.
  const wanted = 'abcdefgh';
  const run: typeof marks = [];
  for (const mark of marks) {
    if (mark.letter === wanted[run.length]) run.push(mark);
  }
  if (run.length < 2) return { text: clean(block), parts: [] };

  const parts: string[] = [];
  for (let i = 0; i < run.length; i++) {
    const from = run[i]!.end;
    const to = i + 1 < run.length ? run[i + 1]!.at : block.length;
    parts.push(clean(block.slice(from, to)));
  }
  return { text: clean(block.slice(0, run[0]!.at)), parts };
}

/**
 * Split the whole text at the facit heading. Returns `[body, answers]`.
 *
 * A heading alone is not enough evidence. Swedish books end a worked example with "Svar:", and
 * splitting there would throw four fifths of the chapter into the answer section — so each
 * candidate is TESTED: a real facit repeats the numbers that came before it, and one that does
 * not repeat them is a word in a paragraph. The best-supported candidate wins, which is also
 * what makes the loose heading pattern safe.
 */
export function splitAtFacit(text: string): { body: string; facit: string; at: number | null } {
  const lines = text.split('\n');
  const nowhere = { body: text, facit: '', at: null };

  const candidates: { at: number; score: number }[] = [];
  lines.forEach((line, index) => {
    if (!FACIT_HEADING.test(line)) return;
    const before = new Set(numbersIn(lines.slice(0, index).join('\n')));
    const after = numbersIn(lines.slice(index + 1).join('\n'));
    if (after.length < 3) return;
    const echoed = after.filter(n => before.has(n)).length / after.length;
    if (echoed < 0.6) return;
    // Length matters as well as agreement: a candidate covering more of the answers is the real
    // heading, and an "Svar:" two exercises from the end can agree perfectly on very little.
    candidates.push({ at: index, score: echoed * Math.log(after.length) });
  });

  const found = candidates.sort((a, b) => b.score - a.score)[0];
  if (!found) return nowhere;
  return {
    body: lines.slice(0, found.at).join('\n'),
    facit: lines.slice(found.at + 1).join('\n'),
    at: found.at,
  };
}

interface Block {
  n: number;
  starred: boolean;
  body: string;
  level: 1 | 2 | 3 | 4 | null;
  section: ParsedSection | null;
  /** Half-open line range within the text `blocks` was given. */
  from: number;
  to: number;
}

/** Read numbered blocks out of a region of text. */
function blocks(text: string): Block[] {
  const lines = text.split('\n');
  const found: Block[] = [];
  let current:
    | {
        n: number; starred: boolean; lines: string[];
        level: 1 | 2 | 3 | 4 | null; section: ParsedSection | null; from: number;
      }
    | null = null;
  let level: 1 | 2 | 3 | 4 | null = null;
  let section: ParsedSection | null = null;
  let at = -1;

  const flush = () => {
    if (!current) return;
    const body = current.lines.join('\n');
    found.push({ ...current, body, to: at });
    current = null;
  };

  for (const line of lines) {
    at += 1;
    const heading = LEVEL_HEADING.exec(line);
    if (heading) {
      // A level heading ends the exercise above it — the band applies to what follows.
      flush();
      level = Number(heading[1]) as 1 | 2 | 3 | 4;
      continue;
    }

    // Checked only where the line is not an exercise, so `1.1.2` never beats `1002.`.
    const inSection = !EXERCISE_START.test(line) && SECTION_HEADING.exec(line);
    if (inSection) {
      flush();
      section = { number: inSection[1]!, title: inSection[2]!.trim() };
      // A new section restarts the level bands, because the book prints them per section.
      level = null;
      continue;
    }

    const start = EXERCISE_START.exec(line);
    if (start) {
      flush();
      current = {
        n: Number(start[2]),
        starred: start[1]!.length > 0 || start[3]!.length > 0,
        lines: [line.slice(start[0].length)],
        level,
        section,
        from: at,
      };
      continue;
    }
    if (current) current.lines.push(line);
  }
  at += 1;
  flush();
  return found;
}

export function parseChapter(raw: string): ParsedChapter {
  const { body, facit, at } = splitAtFacit(raw);

  const answers = new Map<number, string | string[]>();
  for (const block of blocks(facit)) {
    const { text, parts } = splitParts(block.body);
    // The facit prints a dash for a "visa att" exercise: there is nothing to check against.
    const bare = text.replace(/[\u2013\u2014-]/g, '').trim();
    if (parts.length > 0) answers.set(block.n, parts);
    else if (bare.length > 0) answers.set(block.n, text);
    else answers.set(block.n, '');
  }

  const exercises = blocks(body).map<ParsedExercise>(block => {
    const { text, parts } = splitParts(block.body);
    const found = answers.get(block.n);
    return {
      n: block.n,
      text,
      parts,
      lines: { from: block.from, to: block.to },
      level: block.level,
      starred: block.starred,
      section: block.section,
      answer: found === undefined || found === '' ? null : found,
    };
  });

  return { exercises, facitAt: at };
}

/** The range a parsed chapter covers, for creating it in the workspace. */
export const rangeOf = (chapter: ParsedChapter): { from: number; to: number } | null => {
  if (chapter.exercises.length === 0) return null;
  const numbers = chapter.exercises.map(e => e.n);
  return { from: Math.min(...numbers), to: Math.max(...numbers) };
};
